import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { normalizeCustomerName } from '@/lib/customerName';

// Embedded notification sound as base64 (pleasant two-tone chime)
const NOTIFICATION_SOUND_BASE64 = 'data:audio/wav;base64,UklGRtIzAABXQVZFZm10IBAAAAABAAEAIlYAAESsAAACABAAZGF0Ya4zAAAAANUi7ELEXUpxB3w3feB0yGNnS8EtPg127PPNA7SGoMyUepGGljujTrYAzkvoBwMlHNAxlEJ5TRBSclA3SV89NS4rHbkLOvvP7EvhKdmL1D3TydSJ2MHdt+PH6XbvevTA+GP8pP/aAl8GeQpPD9YU0xrUID4mXCpxLNEr+iesIPMVNgg2+AHn5tVUxsC5fLGWrr6xKLuDyvbeKfdfEZkruUOzV71ld2wMa05hvE99N04aYPor2jq8+KJ5kE6GYIXcjSyfA7h41ir4dBqoOkJWH2upd/h65XQGZqdPpTNJFBT0idX8ul6mGpn4kxWX5KFCs5DJ3+IY/SsWQSzVPdhJvk+BT51J9T7AMGIgTQ/c/jfwQ+SK20DWRdQz1XDYR93/4vPon+6w8wb4tPvx/g8CZwVECdMNFxPfGMkeRCSlKDErOys6KN0hHhhOCxb8buuQ2uHKzL2otI+wQ7IVutTHyNq+8RUL5iQhPb5R7GA2aa1p/GF4Uh88hCC1ARHiFMQoqmuWh4qGh8CNzJyOs0XQtfBTEn8ys06/ZPVySHhndLxnZFMVOfUacfsC3fzBX6yendOWD5j+oKOwgMXA3Vz3TBCyJv04CEYsTUhOt0lDQAwzZyO+EmoCn/NH5wDeEtht1bvVcdjh3FbiJ+jM7ebyTfcH+0X+UAGBBCUIcAxwEQAXyxxOIuQm2Sl/KkUozyIFGiIOuP+o7xrfYs/mwfu3x7Ics2W5jsUE17LsGAVmHp02vEvrW6dl5WcvYrFUQEBFJqwIt+nSy2GxjZwSjxuKJo75mqOvj8qm6XoKeCogRzVe9G0wdW5z7WieVg0+QCGKAljk/MiBsoGiA5pvmYagcK7SwfHY2vGLCichDzQNQmBKyEyGSUlBGDU5JgoW5QUB91Tqi+D/2bPWY9aO2JLcveFl5/3sHvKV9l36nv2bAKoDGgclC+EPNhXdGl0gHSVtKKApISiHI6sbsxAaA67zf+PT0wfGb7s3tUO0Ermvw6bTBehq/x0YNDC0RcRW0mG9Ze5hbFbhQ5ErQA8X8WvT';

const POLL_INTERVAL = 30000; // Poll every 30 seconds (reduced from 5s for performance)

export function useOrderNotifications() {
  const [isConnected, setIsConnected] = useState(true);
  const [newOrder, setNewOrder] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const notifiedOrderIdsRef = useRef(new Set());
  const audioRef = useRef(null);
  const lastCheckRef = useRef(new Date().toISOString());

  // Prefer `/notification.wav` when deployed (louder asset); fall back to embedded chime. Volume maxed (browser may still gate until user gesture).
  useEffect(() => {
    let cancelled = false;
    const cleanup = () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };

    try {
      const wav = new Audio('/notification.wav');
      wav.preload = 'auto';
      wav.volume = 1;

      const useFallback = () => {
        if (cancelled) return;
        cleanup();
        try {
          const fallback = new Audio(NOTIFICATION_SOUND_BASE64);
          fallback.volume = 1;
          audioRef.current = fallback;
        } catch (err) {
          console.error('Failed to initialize fallback audio:', err);
        }
      };

      wav.addEventListener('error', useFallback, { once: true });
      audioRef.current = wav;
    } catch (err) {
      console.error('Failed to initialize audio:', err);
      try {
        const fallback = new Audio(NOTIFICATION_SOUND_BASE64);
        fallback.volume = 1;
        audioRef.current = fallback;
      } catch (e2) {
        console.error('Failed to initialize fallback audio:', e2);
      }
    }

    return () => {
      cancelled = true;
      cleanup();
    };
  }, []);

  // Play notification sound
  const playSound = useCallback(() => {
    if (audioRef.current) {
      try {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      } catch (err) {
        // Ignore audio errors
      }
    }
  }, []);

  // Poll for new orders
  useEffect(() => {
    const checkForNewOrders = async () => {
      try {
        // Use authenticated api instance (includes Bearer token from localStorage)
        const response = await api.get('/notifications/new-orders', {
          params: { since: lastCheckRef.current },
        });

        setIsConnected(true);
        const data = response.data;
        
        // Update last check time
        if (data.server_time) {
          lastCheckRef.current = data.server_time;
        }
        
        // Process new orders
        if (data.new_orders && data.new_orders.length > 0) {
          // Filter out already notified orders
          const unseenOrders = data.new_orders.filter(
            order => !notifiedOrderIdsRef.current.has(order.order_id)
          );
          
          if (unseenOrders.length > 0) {
            // Get the most recent new order
            const latestOrder = unseenOrders[0];
            
            console.log('🔔 New order detected:', latestOrder.customer_name);
            
            // Mark all as notified
            unseenOrders.forEach(order => {
              notifiedOrderIdsRef.current.add(order.order_id);
            });
            
            // Set notification state
            setNewOrder({
              orderId: latestOrder.order_id,
              customerName: normalizeCustomerName(latestOrder.customer_name),
              orderSource: latestOrder.order_source,
              message: 'New order received!',
              timestamp: latestOrder.created_at
            });
            
            // Show banner
            setShowBanner(true);
            
            // Play sound
            playSound();
          }
        }
      } catch (error) {
        console.error('Notification poll error:', error);
        setIsConnected(false);
      }
    };

    // Initial check after a short delay
    const initialTimeout = setTimeout(checkForNewOrders, 1000);
    
    // Set up polling interval
    const intervalId = setInterval(checkForNewOrders, POLL_INTERVAL);
    
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(intervalId);
    };
  }, [playSound]);

  // Dismiss banner
  const dismissBanner = useCallback(() => {
    setShowBanner(false);
  }, []);

  // Clear notification (after viewing order)
  const clearNotification = useCallback(() => {
    setNewOrder(null);
    setShowBanner(false);
  }, []);

  return {
    isConnected,
    newOrder,
    showBanner,
    dismissBanner,
    clearNotification
  };
}
